import {
  type Character,
  type ChatContent,
  type ChatMessage as ScenarioChatMessage,
  type EgoCrisisScenario as LoadedScenario,
  type SequenceItem,
} from './storyLoader';
import { getRuntimeScenario } from './scenarioStore';

// Les scénarios BUNDLÉS ont été supprimés (CCT-253) : la table `MISSION_TO_STORY` et son
// index `SCENARIO_BY_KEY` ne servaient qu'à les retrouver. Tout vient désormais du runtime.
export type StoryKey = string;

export function getScenarioByMissionId(missionId: number): LoadedScenario | undefined {
  // Priorité au scénario chargé depuis le BLOB (store runtime) : les chats, ids
  // et personnages doivent venir de la même source que ChapterDetail/SceneReader.
  const rt = getRuntimeScenario(missionId);
  if (rt) {
    return {
      id: rt.id,
      title: rt.title,
      subtitle: rt.subtitle ?? '',
      certification: rt.certification,
      coverImage: undefined as any,
      characters: rt.characters as unknown as Character[],
      chapters: rt.chapters,
    };
  }
  // Rien en base : on ne fabrique rien. L'appelant affiche un état vide.
  return undefined;
}

function getStoryKeyForMission(_missionId: number): string | undefined {
  // La clé d'histoire ne servait qu'à retrouver des avatars BUNDLÉS par mission. Les
  // personnages portent maintenant leur `avatarUrl`, servie par le runtime.
  return undefined;
}

function findChatItem(scenario: LoadedScenario | undefined, itemId: string): SequenceItem | undefined {
  if (!scenario) return undefined;
  for (const chapter of scenario.chapters) {
    for (const item of chapter.sequence) {
      if (item.type === 'chat' && item.id === itemId) return item;
    }
  }
  return undefined;
}

function resolveCharacterImage(
  _storyKey: string | undefined,
  character: Character | undefined,
): any | undefined {
  if (!character) return undefined;
  // SOURCE UNIQUE : la photo servie par le runtime (CCT-253). L'avatar BUNDLÉ passait avant,
  // ce qui faisait gagner un visage embarqué sur celui que l'auteur avait choisi dans la
  // Gallery — et masquait, au passage, toute erreur de résolution côté serveur.
  // Sans photo, l'appelant affiche l'initiale colorée : c'est un repli qui SE VOIT.
  const url = (character as any).avatarUrl;
  return url ? { uri: url } : undefined;
}

function characterInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

export interface ScenarioChat {
  missionId: number;
  missionTitle: string;
  itemId: string;
  tutorName: string;
  tutorRole: string;
  tutorAvatar: string;
  tutorColor?: string;
  tutorImage?: any;
  /** CCT-471 — percentage this chat must reach to count as completed. Absent = no bar.
   *  Resolved by the server from the mission: the app reads one value, never the setting. */
  chatPassPercent?: number;
  /** CCT-471 — show right/wrong on an answer. Absent must read as TRUE: an older payload
   *  carries no flag, and inferring "hide" from its absence would mute every running mission. */
  chatShowVerdict?: boolean;
  messages: Array<{
    id: string;
    sender: 'tutor';
    text: string;
    timestamp: string;
    type?: ScenarioChatMessage['type'];
    options?: string[];
    correctAnswer?: string | string[];
    explanation?: string;
    /** Réaction du personnage par option, indexée par le libellé (CCT-256). */
    optionResponses?: Record<string, string>;
    exploreAll?: boolean;
    characterName?: string;
    characterImage?: any;
    /** CCT-477 — a draw.io diagram carried by this turn, and the author's zoom. */
    diagramUrl?: string;
    diagramZoom?: number;
  }>;
}

/** Load a chat sequence item from the JSON-sourced scenario for a mission. */
export function getScenarioChat(missionId: number, itemId: string | undefined): ScenarioChat | undefined {
  if (!itemId) return undefined;

  const scenario = getScenarioByMissionId(missionId);
  if (!scenario) return undefined;

  const chat = findChatItem(scenario, String(itemId));
  if (!chat) return undefined;

  const content = chat.content as ChatContent;
  if (!Array.isArray(content?.messages) || content.messages.length === 0) return undefined;

  const storyKey = getStoryKeyForMission(missionId);
  const findCharacter = (characterId: string | undefined): Character | undefined =>
    characterId ? scenario.characters.find(c => c.id === characterId) : undefined;

  const tutor = findCharacter(content.tutorCharacterId);
  const firstSpeaker =
    findCharacter(content.messages.find(m => m.characterId)?.characterId) ?? tutor;
  const headerCharacter = firstSpeaker ?? tutor;
  const headerName = headerCharacter?.name ?? 'Tuteur';
  const tutorImage = resolveCharacterImage(storyKey, headerCharacter);

  const messages: ScenarioChat['messages'] = content.messages.map((m, idx) => {
    const speaker = findCharacter(m.characterId) ?? tutor;
    return {
      id: m.id || `${itemId}-m${idx + 1}`,
      sender: 'tutor',
      text: m.text,
      timestamp: m.timestamp ?? '',
      type: m.type ?? 'text',
      options: m.options,
      correctAnswer: m.correctAnswer,
      explanation: m.explanation,
      // CCT-256 : la réaction du personnage PAR choix. Omise ici, elle n'atteignait pas
      // l'écran de chat et le joueur recevait la même explication quelle que soit sa réponse.
      optionResponses: (m as any).optionResponses,
      exploreAll: m.exploreAll === true,
      // CCT-477 — the diagram the author attached to THIS turn. Dropped here, it reached no
      // screen at all, exactly as `optionResponses` once did.
      diagramUrl: (m as any).diagramUrl,
      diagramZoom: (m as any).diagramZoom,
      characterName: speaker?.name,
      characterImage: resolveCharacterImage(storyKey, speaker),
    };
  });

  return {
    missionId,
    missionTitle: chat.title || scenario.title || 'Chat',
    itemId: String(itemId),
    tutorName: headerName,
    tutorRole: headerCharacter?.role ?? tutor?.role ?? '',
    tutorAvatar: characterInitials(headerName),
    tutorColor: headerCharacter?.color ?? tutor?.color,
    tutorImage,
    // CCT-471 — carried by the sequence ITEM, not by the chat content: the server resolves the
    // mission setting onto the item, exactly as it does for `blocking` and `timeLimitSeconds`.
    chatPassPercent: (chat as any).chatPassPercent,
    chatShowVerdict: (chat as any).chatShowVerdict,
    messages,
  };
}
